import React, { FC, memo, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { PrinterStatusResponse, useBridgeApi, useI18n, useLogger, useSelector } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import printErrorIcon from '~Assets/icons/print_error.svg';
import StepFrame from '../../Scenes/Checkout/StepFrame';
import Modal, { MODAL_TRANSITION_MS } from '../Modal';

/** A printer that does not answer within this is treated as fine - never block the kiosk on a silent printer. */
const STATUS_TIMEOUT_MS = 2000;

/** While the notice is up, the printer is asked again this often so it goes away once paper is back. */
const RECHECK_MS = 10000;

/** Same entrance/exit pattern as ClearCartConfirm: keyframe in, CSSTransition-driven out. */
/** The card: scales and fades in, and back out. StepFrame provides its own padding. */
const Card = styled.div`
  position: relative;
  width: min(100%, 860px);
  border-radius: 36px;
  background: var(--eposSurface);
  color: var(--eposText);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.45);
  transform: translate3d(0, 0, 0) scale(1);
  opacity: 1;
  animation: eposPaperOutCardIn ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity ${MODAL_TRANSITION_MS}ms ease-out;
  will-change: transform, opacity;

  .epos-paper-out-exit-active &,
  .epos-paper-out-exit-done & {
    animation: none;
    transform: translate3d(0, 24px, 0) scale(0.96);
    opacity: 0;
  }

  @keyframes eposPaperOutCardIn {
    from {
      transform: translate3d(0, 24px, 0) scale(0.96);
      opacity: 0;
    }
    to {
      transform: translate3d(0, 0, 0) scale(1);
      opacity: 1;
    }
  }
`;

/** The icon on a padded plate - same box as the order number on DoneStep. */
const IconPlate = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 56px;
  border-radius: 32px;
  background: var(--colorBlueGray100);
`;

interface Props {
  /** Bumped after every completed purchase - each bump asks the primary printer for its paper status. */
  checkKey: number;
}

/**
 * Tells the shopper the ticket printer is out of paper and to get the staff.
 * After each purchase the primary printer is asked for its status through
 * the native bridge (the same call the printer troubleshooting uses); while
 * the notice is up it keeps asking, and drops the notice once paper is back.
 * Cannot be dismissed by the shopper - only fresh paper closes it.
 */
const PaperOutNotice: FC<Props> = ({ checkKey }) => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-paper-out');
  const bridgeApi = useBridgeApi();
  const primaryPrinterId = useSelector(
    (state) => state.printers.find((printer) => printer.primary)?.id,
  );

  const [paperEmpty, setPaperEmpty] = useState(false);

  /** Asks the printer; a timeout or error counts as paper present so a silent printer never blocks the kiosk. */
  const checkPaper = async (printerId: string): Promise<boolean> => {
    const timeout: Promise<PrinterStatusResponse> = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), STATUS_TIMEOUT_MS),
    );
    try {
      const statuses = await Promise.race([bridgeApi.getPrinterStatus(printerId), timeout]);
      logger.info('Printer paper status', { printerId, statuses });
      return Boolean(statuses?.paperEmpty);
    } catch (error) {
      logger.warn('Printer status check failed - assuming paper is present', {
        printerId,
        reason: error instanceof Error ? error.message : 'Unknown error',
      });
      return false;
    }
  };

  // One check per completed purchase.
  useEffect(() => {
    if (checkKey === 0 || !primaryPrinterId) {
      return undefined;
    }
    let cancelled = false;
    checkPaper(primaryPrinterId).then((empty) => {
      if (cancelled) return;
      if (empty)
        logger.warn('Primary printer is out of paper - showing notice', { primaryPrinterId });
      setPaperEmpty(empty);
    });
    return () => {
      cancelled = true;
    };
  }, [checkKey, primaryPrinterId]);

  // While the notice is up, keep asking so it disappears once staff have refilled.
  useEffect(() => {
    if (!paperEmpty || !primaryPrinterId) {
      return undefined;
    }
    const timer = setInterval(async () => {
      const empty = await checkPaper(primaryPrinterId);
      if (!empty) {
        logger.info('Paper is back - hiding notice', { primaryPrinterId });
        setPaperEmpty(false);
      }
    }, RECHECK_MS);
    return () => clearInterval(timer);
  }, [paperEmpty, primaryPrinterId]);

  return (
    <Modal open={paperEmpty} exitPrefix="epos-paper-out" zIndex={40} testId="epos-paper-out">
      <Card>
        <StepFrame
          testId="epos-paper-out-step"
          title={i18n('Express.PaperOut.Title')}
          subtitle={i18n('Express.PaperOut.Body')}>
          <IconPlate>
            <Icon src={printErrorIcon} color="var(--eposText)" width="120px" height="120px" />
          </IconPlate>
        </StepFrame>
      </Card>
    </Modal>
  );
};

export default memo(PaperOutNotice);
